import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

// Shared YouTube/course flow for fetching, selecting, and saving videos
// Params:
// - maxResults: number of videos to fetch (default 3)
// - token: auth token for API calls
// - product: feature context for subscription gating ('resume' | 'exam'), defaults to 'resume'
// - onSubscriptionBlocked?: callback invoked when backend returns subscription_required
export default function useYoutubeCourses({ maxResults = 3, token, product = 'resume', onSubscriptionBlocked } = {}) {
  const [youtubeVideoId, setYoutubeVideoId] = useState(null);
  const [youtubeVideos, setYoutubeVideos] = useState([]);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);

  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedDuration, setSelectedDuration] = useState('long');
  const durationToValue = useMemo(() => (d) => (d === 'short' ? 0 : d === 'medium' ? 1 : 2), []);
  const [selectedDurationValue, setSelectedDurationValue] = useState(durationToValue('long'));

  const [ytLoading, setYtLoading] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(false);

  const [savingCourse, setSavingCourse] = useState(false);
  const [courseSaved, setCourseSaved] = useState(false);
  const [savedVideoIds, setSavedVideoIds] = useState(new Set());
  const [savedByVideo, setSavedByVideo] = useState({}); // videoId -> course _id

  const [lastFetchedKey, setLastFetchedKey] = useState(null);

  // Ensure a selected video is chosen when list updates
  useEffect(() => {
    if (Array.isArray(youtubeVideos) && youtubeVideos.length > 0) {
      setSelectedVideoIndex(0);
      const first = youtubeVideos[0];
      if (first && first.videoId) setYoutubeVideoId(first.videoId);
    }
  }, [youtubeVideos]);

  // Keep saved toggle in sync with currently focused video
  useEffect(() => {
    const currentId = (youtubeVideos?.[selectedVideoIndex]?.videoId) || youtubeVideoId || null;
    if (currentId && savedVideoIds && savedVideoIds.has(currentId)) setCourseSaved(true);
    else setCourseSaved(false);
  }, [youtubeVideoId, selectedVideoIndex, youtubeVideos, savedVideoIds]);

  // Invalidate cached fetch key and clear previous results to avoid re-using
  // stale videos when a new query/topic is requested.
  const invalidateFetchCache = useCallback(() => {
    setLastFetchedKey(null);
    setYoutubeVideos([]);
    setYoutubeVideoId(null);
    setSelectedVideoIndex(0);
  }, []);

  const fetchSavedCourses = useCallback(async () => {
    try {
      const resp = await axios.get('/user/courses', { headers: { 'x-access-token': token } });
      const items = (resp?.data?.items) || [];
      const ids = new Set(items.map(it => it?.videoId).filter(Boolean));
      const map = {};
      items.forEach(it => { if (it?.videoId && it?._id) map[it.videoId] = it._id; });
      setSavedVideoIds(ids);
      setSavedByVideo(map);
    } catch (_) {
      setSavedVideoIds(new Set());
      setSavedByVideo({});
    }
  }, [token]);

  const fetchVideos = useCallback(async (query, force = false) => {
    if (!query) return false;
    const regionCode = selectedCountry || '';
    const videoDuration = selectedDuration || 'long';
    const currentKey = `${query}::${regionCode}::${videoDuration}::${maxResults}`;
    if (!force && lastFetchedKey === currentKey) return true; // already fetched
    try {
      setYtLoading(true);
      let url = `/youtube/search?q=${encodeURIComponent(query)}&maxResults=${encodeURIComponent(String(maxResults))}`;
      if (regionCode) url += `&regionCode=${encodeURIComponent(regionCode)}`;
      if (videoDuration) url += `&videoDuration=${encodeURIComponent(videoDuration)}`;
      if (product) url += `&product=${encodeURIComponent(product)}`;
      const resp = await axios.get(url, { headers: { 'x-access-token': token } });
      if (resp?.data?.ok && Array.isArray(resp.data.items) && resp.data.items.length > 0) {
        setYoutubeVideos(resp.data.items);
        const first = resp.data.items[0];
        if (first && first.videoId) setYoutubeVideoId(first.videoId);
        setLastFetchedKey(currentKey);
        return true;
      }
      setYoutubeVideos([]);
      setLastFetchedKey(currentKey);
      return false;
    } catch (e) {
      const status = e?.response?.status;
      const message = e?.response?.data?.message;
      if (status === 403 && message === 'subscription_required') {
        onSubscriptionBlocked && onSubscriptionBlocked();
      }
      return false;
    } finally {
      setYtLoading(false);
    }
  }, [lastFetchedKey, maxResults, selectedCountry, selectedDuration, token, product, onSubscriptionBlocked]);

  const toggleSaveCurrent = useCallback(async ({ title = 'Course', description = '', thumbnail = '', sourceQuery = '' } = {}) => {
    if (savingCourse) return { ok: false, action: 'noop' };
    const current = youtubeVideos?.[selectedVideoIndex] || {};
    const currentId = current?.videoId || youtubeVideoId;
    if (!currentId) return { ok: false, action: 'noop' };

    // Unsave
    if (savedVideoIds?.has(currentId)) {
      const docId = savedByVideo?.[currentId];
      if (!docId) return { ok: false, action: 'noop' };
      try {
        setSavingCourse(true);
        const del = await axios.delete(`/user/courses/${docId}`, { headers: { 'x-access-token': token } });
        if (del?.data?.ok && del?.data?.deleted) {
          setCourseSaved(false);
          setSavedVideoIds(prev => {
            const next = new Set(prev);
            next.delete(currentId);
            return next;
          });
          setSavedByVideo(prev => {
            const next = { ...(prev || {}) };
            delete next[currentId];
            return next;
          });
          return { ok: true, action: 'unsaved' };
        }
        return { ok: false, action: 'unsave_failed' };
      } catch (_) {
        return { ok: false, action: 'unsave_failed' };
      } finally {
        setSavingCourse(false);
      }
    }

    // Save
    try {
      setSavingCourse(true);
      const payload = {
        provider: 'youtube',
        videoId: currentId,
        title: current?.title || title,
        description: current?.description || description,
        thumbnail: current?.thumbnail || thumbnail,
        link: `https://www.youtube.com/watch?v=${currentId}`,
        regionCode: selectedCountry || null,
        durationPreference: selectedDuration,
        sourceQuery,
      };
      const resp = await axios.post('/user/courses/save', payload, { headers: { 'x-access-token': token } });
      if (resp?.data?.ok) {
        setCourseSaved(true);
        if (currentId) {
          setSavedVideoIds(prev => new Set([...(prev || new Set()), currentId]));
          const cid = resp?.data?.course?._id;
          if (cid) setSavedByVideo(prev => ({ ...(prev || {}), [currentId]: cid }));
        }
        return { ok: true, action: 'saved' };
      }
      return { ok: false, action: 'save_failed' };
    } catch (_) {
      setCourseSaved(false);
      return { ok: false, action: 'save_failed' };
    } finally {
      setSavingCourse(false);
    }
  }, [savingCourse, youtubeVideos, selectedVideoIndex, youtubeVideoId, savedVideoIds, savedByVideo, selectedCountry, selectedDuration, token]);

  return {
    // state
    youtubeVideoId, setYoutubeVideoId,
    youtubeVideos,
    selectedVideoIndex, setSelectedVideoIndex,
    selectedCountry, setSelectedCountry,
    selectedDuration, setSelectedDuration,
    selectedDurationValue, setSelectedDurationValue,
    durationToValue,
    ytLoading,
    iframeLoading, setIframeLoading,
    savingCourse,
    courseSaved,
    savedVideoIds,
    savedByVideo,
    // actions
    invalidateFetchCache,
    fetchSavedCourses,
    fetchVideos,
    toggleSaveCurrent,
  };
}
